import * as React from 'react';
import { observer } from 'mobx-react';

import { useLocation, useNavigate } from 'react-router-dom';
import {
    ICardProps,
    TextAreaFieldItem,
    IconTextButton,
    showConfirmDialogWithoutCancel,
    showInformationDialog,
} from 'ui-lib';

import { useDeviceStore, useRoutesStore, useStores } from '../../../../store';
import { IShellCommandExecutionResponse } from '../../../../dto/device-dto';
import { DEVICES_ROUTE_CODES } from '../';
import { getErrorMessage } from 'common-utils';
import { TableUtils } from 'pages/private/components/table-utils';
import { ISendCommandSubmitFormValues, SendCommandForm } from '../file-transfer/send-command-form';
import { DEFAULT_DEVICE_COLUMN, DevicesTableComponent } from 'pages/private/components/devices-table-component';
import shellPromptCss from './shellprompt.css';

interface IShellPromptOptions {
    deviceName: string;
    deviceGroup: string;
    shellCommand: string;
}
interface IShellPromptComponentProps {
    isEdit?: boolean;
    uid?: string;
    deviceGroupName?: string;
    cardPros?: ICardProps;
    shellPromptInfo: IShellPromptOptions;
}

export const ShellPromptExecution = observer((props: IShellPromptComponentProps) => {
    const devicesStore = useDeviceStore();

    const stores = useStores();
    const navigate = useNavigate();
    const state = useLocation().state as any ?? {};
    const [displayCommandOutput, setDisplayCommandOutput] = React.useState<IShellCommandExecutionResponse[]>([]);
    const [isProcessing, setIsProcessing] = React.useState(false);

    const routeStore = useRoutesStore();
    const shellCommandRoute = routeStore.getRoute(DEVICES_ROUTE_CODES.SHELL_COMMAND);

    return (
        <SendCommandForm
            loading={isProcessing}
            onSubmit={async (options: ISendCommandSubmitFormValues) => {
                try {
                    setIsProcessing(true);
                    const { varOrClientAccountId, deviceGroupName, deviceName, formValues, enableReferenceLink } =
                        options;

                    const result = await devicesStore.shellCommandExecution(varOrClientAccountId, {
                        deviceName: deviceName,
                        deviceGroup: deviceGroupName,
                        command: formValues.shellCommandField,
                    });

                    setDisplayCommandOutput(result);
                    if (deviceGroupName) {
                        enableReferenceLink();
                    }
                    return showConfirmDialogWithoutCancel({
                        title: 'Success',
                        content: 'Command completed successfully',
                        okText: 'OK',
                        onOk: () => navigate(shellCommandRoute.path),
                    });
                } catch (err) {
                    return showInformationDialog({
                        modalType: 'error',
                        title: 'Error',
                        content: getErrorMessage(err),
                    });
                } finally {
                    setIsProcessing(false);
                }
            }}
            deviceGroupName={state.deviceGroupName}
            uid={state.uid}
            extraFormItems={[
                <TextAreaFieldItem
                    labelColSpan={5}
                    code='shellCommandField'
                    label='Command'
                    labelAlign='right'
                    isRequired={true}
                    initialValue={props.shellPromptInfo.shellCommand}
                    textAreaProps={{ placeholder: 'shell prompt', autoSize: { minRows: 5, maxRows: 10 } }}
                />,
                <IconTextButton
                    className={shellPromptCss.control}
                    key='save'
                    label='Send command'
                    type='primary'
                    htmlType='submit'
                    loading={isProcessing}
                />,
            ]}
            extra={
                <DevicesTableComponent
                    dataSource={displayCommandOutput.map((item) => ({ ...item, UID: item.deviceName }))}
                    title='Shell command response'
                    buildDeviceLinkProps={(record) => ({ accountId: stores.commonStore.selectedOrganisation })}
                    hiddenDefaultColumns={[
                        DEFAULT_DEVICE_COLUMN.DEVICE_GROUP,
                        DEFAULT_DEVICE_COLUMN.LAST_UPDATE,
                        DEFAULT_DEVICE_COLUMN.ESN,
                        DEFAULT_DEVICE_COLUMN.SERIAL,
                        DEFAULT_DEVICE_COLUMN.TYPE,
                    ]}
                    extraColumns={[
                        TableUtils.createBasicFilterColumn({ key: 'output', title: 'Output' }),
                        TableUtils.createBasicFilterColumn({ key: 'error', title: 'Error' }),
                    ]}
                    exportOptions={{
                        hideExportItem: false,
                    }}
                />
            }
        />
    );
});

export function ShellCommandExecution() {
    return (
        <ShellPromptExecution
            shellPromptInfo={{
                deviceName: '',
                deviceGroup: '',
                shellCommand: '',
            }}
        />
    );
}
